import type { PaperSize, PrintLayout } from "./printLayout";
import { resolveCssFontFamily } from "./fonts";
import { cssUrl } from "./cssUrl";

export const SEAL_RESERVE_BOTTOM_MM = 38;
export const PAGE_SIZES_MM: Record<PaperSize, { width: number; height: number }> = {
  A4: { width: 210, height: 297 },
  A5: { width: 148, height: 210 },
  Letter: { width: 216, height: 279 },
};

export function buildDocumentCss(layout: PrintLayout, opts: { reserveSealSpace: boolean; preview?: boolean }): string {
  const size = PAGE_SIZES_MM[layout.paper];
  const bottom = opts.reserveSealSpace ? SEAL_RESERVE_BOTTOM_MM : layout.marginBottomMm;
  const margin = `${layout.marginTopMm}mm ${layout.marginSideMm}mm ${bottom}mm ${layout.marginSideMm}mm`;
  const contentMinHeight = `calc(${size.height}mm - ${layout.marginTopMm}mm - ${bottom}mm)`;
  const bg = cssUrl(layout.backgroundImageUrl);
  return `
  :root {
    --doc-text: ${layout.textColor};
    --doc-title: ${layout.titleColor};
    --doc-font: ${resolveCssFontFamily(layout.fontId)};
    --doc-scale: ${layout.fontSizePt / 10};
    --doc-lh: ${layout.lineHeight};
    --doc-rule: #E7E2DB;
    --doc-rule-soft: #EFEAE3;
    --doc-box: #CFC9C1;
    --doc-tint: #FAF8F4;
  }
  @page { size: ${size.width}mm ${size.height}mm; margin: ${margin}; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { color: var(--doc-text); font-family: var(--doc-font); font-size: calc(12px * var(--doc-scale)); -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  * { box-sizing: border-box; }
  .doc-sheet { position: relative; isolation: isolate; display: flex; flex-direction: column; gap: calc(14px * var(--doc-scale)); min-height: ${contentMinHeight}; }
  .doc-sheet::before { content: ""; position: fixed; z-index: -1; top: -${layout.marginTopMm}mm; right: -${layout.marginSideMm}mm; bottom: -${bottom}mm; left: -${layout.marginSideMm}mm; background-image: ${bg}; background-size: 100% 100%; background-position: center; background-repeat: no-repeat; pointer-events: none; }
  .doc-spacer { flex: 1 1 auto; }
  .doc-spacer ~ .doc-spacer { display: none; }
  .doc-header { padding: 6px 8px 14px; border-bottom: 1.5px solid var(--doc-rule); }
  .doc-header__row { display: flex; align-items: center; gap: 16px; }
  .doc-header--inline-apart .doc-header__row { justify-content: space-between; }
  .doc-header--inline-apart .doc-header__text { text-align: right; }
  .doc-header__logo { height: var(--doc-logo-h); max-width: 160px; object-fit: contain; flex-shrink: 0; }
  .doc-header--center { display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
  .doc-header--center .doc-header__logo { height: auto; max-height: calc(var(--doc-logo-h) + 4px); max-width: 220px; }
  .doc-header--image-text .doc-header__row, .doc-header--text-image .doc-header__row { gap: 20px; }
  .doc-header--text-image .doc-header__row { flex-direction: row-reverse; }
  .doc-header__split-image { flex: 1; height: 84px; display: flex; align-items: center; justify-content: center; }
  .doc-header__split-image img { max-width: 100%; max-height: 84px; object-fit: contain; }
  .doc-header--image-text .doc-header__text { flex: 1; text-align: right; }
  .doc-header--text-image .doc-header__text { flex: 1; text-align: left; }
  .doc-header--full { padding: 0 0 8px; }
  .doc-header--full img { width: 100%; height: auto; display: block; }
  .doc-header__text { font-size: calc(11.5px * var(--doc-scale)); line-height: 1.45; min-width: 0; }
  .doc-footer { border-top: 1.5px solid var(--doc-rule); padding: 8px 8px 0; font-size: calc(10.5px * var(--doc-scale)); opacity: .85; display: flex; flex-direction: column; gap: 6px; }
  .doc-digital-seal { position: absolute; right: 0; bottom: -26mm; left: 0; min-height: 26mm; padding-top: 2.5mm; border-top: .25mm solid #CFC9C1; color: #302D2A; font: 6.5pt Helvetica, Arial, sans-serif; }
  .doc-digital-seal__row { display: flex; align-items: flex-start; justify-content: space-between; gap: 5mm; }
  .doc-digital-seal__text { display: flex; flex: 1; flex-direction: column; gap: .6mm; min-width: 0; }
  .doc-digital-seal__text p { margin: 0; line-height: 1.35; }
  .doc-digital-seal__detail { font-weight: 600; }
  .doc-digital-seal__qr { width: 12mm; height: 12mm; flex: none; padding: .4mm; background: #fff; }
  .doc-digital-seal__qr svg { display: block; width: 100%; height: 100%; fill: #211F1D; }
  @media screen {
    html, body { background: transparent; }
    .doc-sheet { width: ${size.width}mm; min-height: ${size.height}mm; padding: ${margin}; background-color: #fff; }
    .doc-sheet::before { position: absolute; inset: 0; }
    .doc-digital-seal { right: ${layout.marginSideMm}mm; bottom: 12mm; left: ${layout.marginSideMm}mm; }
${opts.preview ? "    .doc-preview-counter { position: absolute; left: 0; right: 0; bottom: 5.6mm; text-align: center; font: 7pt Helvetica, Arial, sans-serif; color: #8C8C8C; letter-spacing: .02em; }\n" : ""}  }
${opts.preview ? "  @media print { .doc-preview-counter { display: none; } }\n" : ""}  `;
}
